import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CONVEYANCE_TYPES, type Location, type Strings } from '@koala/sdk';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { type IBasketFulfillment } from '@/types/fulfillment';
import { settings } from '@/components/locations/map/google/map-config';
import {
  StyledMap,
  StyledMapOuter,
  StyledMapSearchButton,
} from '@/components/locations/map/styles';
import { MapTooltip } from '@/components/locations/map/tooltip';
import * as ErrorReporter from '@/utils/errorReporter';
import { useGoogleMapsLoader } from '@/utils/googleMapsLoader';

/**
 * Selected-pin deltas from the TEAM-14843 design (TEAM-15027). The default pin stays Google's
 * default; only the selected one grows, darkens and lifts above its neighbours.
 */
const SELECTED_PIN_SCALE = 1.38;
const SELECTED_PIN_OPTIONS: google.maps.marker.PinElementOptions = {
  scale: SELECTED_PIN_SCALE,
  background: '#B42121',
  borderColor: '#251F1F',
  glyphColor: '#FFFFFF',
};
const SELECTED_PIN_SHADOW = 'drop-shadow(0 3px 8px rgba(23, 20, 18, 0.35))';
const SELECTED_PIN_Z_INDEX = 1000;
const DEFAULT_PIN_HEIGHT_PX = 40;
/** The InfoWindow sits on the location's lat/lng, so it is lifted clear of the selected pin. */
const INFO_WINDOW_OFFSET_Y = -Math.round(DEFAULT_PIN_HEIGHT_PX * SELECTED_PIN_SCALE);

const createPinContent = (
  PinElement: typeof google.maps.marker.PinElement,
  selected: boolean,
): HTMLElement => {
  const pin = new PinElement(selected ? SELECTED_PIN_OPTIONS : undefined);
  if (selected) {
    pin.element.style.filter = SELECTED_PIN_SHADOW;
  }
  return pin.element;
};

const applyPinState = (
  marker: google.maps.marker.AdvancedMarkerElement,
  PinElement: typeof google.maps.marker.PinElement,
  selected: boolean,
) => {
  marker.content = createPinContent(PinElement, selected);
  marker.zIndex = selected ? SELECTED_PIN_Z_INDEX : null;
};

interface IMap {
  locations: Location[];
  activeLocationId: number | null;
  setActiveLocation: (id: number) => void;
  toggleFulfillmentModal: (show: boolean, location: Location, conveyanceType: string) => void;
  fetchCenterCoords: (center: number[], radius: number) => void;
  strings: Strings;
  basketFulfillment: IBasketFulfillment;
}

const LocationsMap = ({
  locations,
  activeLocationId,
  setActiveLocation,
  toggleFulfillmentModal,
  fetchCenterCoords,
  strings,
  basketFulfillment,
}: IMap) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const markersRef = useRef<Map<number, google.maps.marker.AdvancedMarkerElement>>(new Map());
  const pinElementRef = useRef<typeof google.maps.marker.PinElement | null>(null);
  /** The marker currently wearing the selected pin, so only it is restyled on the next change. */
  const selectedMarkerIdRef = useRef<number | null>(null);
  /** Bumped each time the markers are rebuilt so the selected-pin effect re-applies. */
  const [markersVersion, setMarkersVersion] = useState(0);
  const mapOptions = {
    ...settings,
    disableDefaultUI: true,
  };
  delete mapOptions.center;
  delete mapOptions.zoom;

  const onLoad = useCallback((map: google.maps.Map) => setMap(map), []);
  const onMount = useCallback(() => setMap(null), []);

  const activeLocation = locations?.find((location) => location.id === activeLocationId);

  const locationMarkers = useMemo(
    () =>
      locations?.length
        ? locations.map((location) => {
            return {
              id: location.id,
              label: location.label,
              position: {
                lat: location.latitude,
                lng: location.longitude,
              },
            };
          })
        : [],
    [locations],
  );

  const { isLoaded } = useGoogleMapsLoader();

  const mapIsReady = Boolean(isLoaded && map);

  const handleBoundsChanged = () => {
    if (map) {
      setBounds(map.getBounds() ?? null);
    }
  };

  const handleSearchArea = () => {
    if (!bounds) {
      return;
    }

    const sw = bounds.getSouthWest(); // Bottom-left corner
    const mapCenter = map?.getCenter(); // Center

    if (!mapCenter?.lat() || !mapCenter?.lng() || !sw.lat() || !sw.lng()) {
      return;
    }

    // Approximate degrees to miles
    const horizontalDistance = (mapCenter?.lng() - sw.lng()) * 69.2;
    const verticalDistance = (mapCenter?.lat() - sw.lat()) * 69.2;
    const largestRadialDistance: number = Math.max(horizontalDistance, verticalDistance);

    fetchCenterCoords([mapCenter?.lat(), mapCenter?.lng()], largestRadialDistance);

    setShowSearchAreaButton(false);
  };

  useEffect(() => {
    try {
      if (map && locationMarkers.length) {
        const bounds = new google.maps.LatLngBounds();
        locationMarkers.forEach((marker) => {
          bounds.extend(marker.position);
        });
        map.fitBounds(bounds);
      }
    } catch (error) {
      ErrorReporter.captureException(error, 'error');
    }
  }, [map, locationMarkers]);

  useEffect(() => {
    if (!mapIsReady) return;

    // The selected-marker ref is reset with the markers, so the selection effect re-applies.
    const clearMarkers = () => {
      markersRef.current.forEach((m) => {
        m.map = null;
      });
      markersRef.current = new Map();
      selectedMarkerIdRef.current = null;
    };

    clearMarkers();

    if (!locationMarkers?.length) return;

    let cancelled = false;

    google.maps
      .importLibrary('marker')
      .then((markerLib) => {
        if (cancelled) return;
        const { AdvancedMarkerElement, PinElement } = markerLib as google.maps.MarkerLibrary;
        pinElementRef.current = PinElement;

        const markers = new Map<number, google.maps.marker.AdvancedMarkerElement>();
        locationMarkers.forEach((marker) => {
          const advancedMarker = new AdvancedMarkerElement({
            map,
            position: { lat: marker.position.lat, lng: marker.position.lng },
            // Explicit default pin, so a deselected marker can be restored to it.
            content: createPinContent(PinElement, false),
          });
          advancedMarker.addListener('gmp-click', () => {
            setActiveLocation(marker.id);
          });
          markers.set(marker.id, advancedMarker);
        });
        markersRef.current = markers;
        setMarkersVersion((version) => version + 1);
      })
      .catch((error) => {
        // A duplicate Google Maps load makes AdvancedMarkerElement fail its instanceof check here
        // (TEAM-13351). Report it so the error class is visible past Sentry's maps.googleapis.com filter.
        ErrorReporter.reportGoogleMapsError(error, { component: 'locations-map' });
      });

    return () => {
      cancelled = true;
      clearMarkers();
    };
  }, [mapIsReady, locationMarkers, map, setActiveLocation]);

  /**
   * Selected-pin state (TEAM-15027). Kept out of the marker effect above: that effect rebuilds
   * every marker, and running it on each selection would churn markers and reopen the TEAM-13304
   * race. This only swaps the pin on the previously and newly selected markers.
   */
  useEffect(() => {
    const PinElement = pinElementRef.current;
    const previousId = selectedMarkerIdRef.current;
    if (!PinElement || previousId === activeLocationId) return;

    const markers = markersRef.current;
    const previous = previousId !== null ? markers.get(previousId) : undefined;
    if (previous) {
      applyPinState(previous, PinElement, false);
    }

    const next = activeLocationId ? markers.get(activeLocationId) : undefined;
    if (next) {
      applyPinState(next, PinElement, true);
    }

    selectedMarkerIdRef.current = next ? activeLocationId : null;
  }, [activeLocationId, markersVersion]);

  return (
    <>
      {isLoaded && !window.matchMedia(`(max-width: 767px)`).matches ? (
        <StyledMapOuter>
          {showSearchAreaButton && (
            <StyledMapSearchButton onClick={handleSearchArea} $buttonTheme="secondary">
              Search This Area
            </StyledMapSearchButton>
          )}
          <StyledMap>
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100%',
              }}
              center={settings.center as google.maps.LatLngLiteral}
              zoom={settings.zoom ?? 10}
              options={{
                ...mapOptions,
                mapId: process.env.NEXT_PUBLIC_GOOGLE_MAP_ID,
                zoomControlOptions: {
                  position: google.maps.ControlPosition.TOP_RIGHT,
                },
              }}
              onLoad={onLoad}
              onUnmount={onMount}
              onBoundsChanged={() => handleBoundsChanged()}
              onDragEnd={() => {
                handleBoundsChanged();
                setShowSearchAreaButton(true);
              }}
            >
              {activeLocation && (
                <InfoWindow
                  options={{
                    pixelOffset: new window.google.maps.Size(0, INFO_WINDOW_OFFSET_Y),
                  }}
                  position={{
                    lat: activeLocation?.latitude ?? 0,
                    lng: activeLocation?.longitude ?? 0,
                  }}
                  onCloseClick={() => setActiveLocation(0)}
                >
                  <MapTooltip
                    location={activeLocation}
                    toggleFulfillmentModal={() =>
                      toggleFulfillmentModal(true, activeLocation, CONVEYANCE_TYPES.DELIVERY)
                    }
                    strings={strings}
                    basketFulfillment={basketFulfillment}
                  />
                </InfoWindow>
              )}
            </GoogleMap>
          </StyledMap>
        </StyledMapOuter>
      ) : null}
    </>
  );
};

export default LocationsMap;
